import { useCallback, useState } from 'react';
import { useMediaQuery } from '@/hooks/use-mobile';
import type { SheetSnap } from '@/lib/app-editor/editor-sheet';

/** >= 1024px: canvas + inspector. Below: canvas + bottom sheet (AD-016, D4). */
export const EDITOR_DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

export interface EditorChrome<TId extends string> {
  activeTab: TId;
  setActiveTab: (id: TId) => void;
  snap: SheetSnap;
  setSnap: (snap: SheetSnap) => void;
  /**
   * Tap on an element of the canvas: switches to its tab and brings the sheet
   * to the medium height (on the desktop it only switches the tab).
   */
  focusTarget: (id: TId) => void;
}

export function useEditorChrome<TId extends string>(
  initialTab: TId
): EditorChrome<TId> {
  const isDesktop = useMediaQuery(EDITOR_DESKTOP_MEDIA_QUERY);
  const [activeTab, setActiveTab] = useState<TId>(initialTab);
  const [snap, setSnap] = useState<SheetSnap>('medium');

  const focusTarget = useCallback(
    (id: TId) => {
      setActiveTab(id);
      if (!isDesktop) setSnap('medium');
    },
    [isDesktop]
  );

  return { activeTab, setActiveTab, snap, setSnap, focusTarget };
}
